<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import { licenseModule } from '@/store/license/module'
import { studentModule } from '@/store/student/module'
import { examModule } from '@/store/exam/module'
import { organizationModule } from '@/store/organization/module'
import { bundleModule } from '@/store/bundle/module'
import { toastModule } from '@/store/toast/module'

type TStudentRemind = {
    orgStudentId: string
    email: string
}

const {
    SidePanel,
    Button: PocketButton,
    Textarea: PocketTextarea,
} = UIKit

const props = defineProps<{
    orgStudentIds: string[]
}>()

const emit = defineEmits<{ close: [value?: boolean] }>()

const isLoading = ref(false)
const customMessage = ref('')

const currentOrgLicenses = computed(() => licenseModule.getters.getCurrentOrgLicenses())
const currentOrgStudents = computed(() => studentModule.getters.getCurrentOrgStudents())
const currentOrgStudentUsers = computed(() => studentModule.getters.getCurrentOrgStudentUsers())
const currentOrganization = computed(() => organizationModule.getters.getCurrentOrganization())

const licenses = computed(() => {
    return currentOrgLicenses.value
        .filter(l => l.orgStudent && props.orgStudentIds.includes(l.orgStudent.objectId))
        .sort((a, b) => a.status === 'consumed' ? -1 : a.status === 'pending' && b.status === 'archived' ? -1 : 1)
})

const studentsToRemind = computed(() => {
    return props.orgStudentIds.reduce<TStudentRemind[]>((acc, osi) => {
        const orgStudent = currentOrgStudents.value[osi]
        const orgStudentUser = orgStudent?.user && currentOrgStudentUsers.value[orgStudent.user.objectId]
        if (orgStudentUser && orgStudentUser.username) {
            // Have a user existing
            acc.push({
                orgStudentId: osi,
                email: orgStudentUser.username,
            })
        } else {
            // Extract email from license
            const license = licenses.value.find(l => l.orgStudent?.objectId === osi)
            if (license && license.userEmail) {
                acc.push({
                    orgStudentId: osi,
                    email: license.userEmail,
                })
            }
        }
        return acc
    }, [] as TStudentRemind[])
})

const emitClose = () => {
    emit('close', true)
}

const submitUpdatePendingStudent = async () => {
    const orgStudentId = licenses.value?.[0]?.orgStudent?.objectId
    const currentOrganizationVal = currentOrganization.value

    if (!orgStudentId) {
        throw new Error('submitUpdatePendingStudent: Unable to find orgStudent ID')
    }

    if (!currentOrganizationVal) {
        throw new Error('submitUpdatePendingStudent: Unable to find current organization')
    }

    isLoading.value = true

    const numStudentsToRemind = studentsToRemind.value.length

    const invites = studentsToRemind.value.map(s => ({
        orgStudentId: s.orgStudentId,
        email: s.email,
        customMessage: customMessage.value,
    }))

    await studentModule.actions.resendInvites({
        organizationId: currentOrganizationVal.objectId,
        invites,
    })

    isLoading.value = false
    emitClose()
    toastModule.actions.displayToast({
        title: `${numStudentsToRemind} Invite(s) sent.`,
    })
}

onMounted(async () => {
    await Promise.all([
        licenseModule.actions.fetchCurrentOrgLicenses(),
        examModule.actions.fetchCurrentOrgExams(),
        examModule.actions.fetchExamMetadata(),
        organizationModule.actions.fetchCurrentOrganization(),
        studentModule.actions.fetchCurrentOrgStudents(),
        studentModule.actions.fetchCurrentOrgStudentUsers(),
        bundleModule.actions.fetchBundles(),
    ])
})
</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            name="Resend Invite"
            class="resend-invite ri"
            :custom-content-styles="{ padding: '0', height: 'calc(100% - 146px)' }"
            :isTeachSidePanel="true"
            @close="emitClose"
        >
            <div class="ri__bottom">
                <div class="ri__personalize">
                    <label class="ri__label">
                        Personalize
                    </label>
                    <p class="ri__note">
                        Add a note to the invite email (optional)
                    </p>
                    <PocketTextarea
                        v-model="customMessage"
                        height="114px"
                        class="ri__text-field"
                    />
                </div>
            </div>
            <template #action>
                <PocketButton
                    type="primary"
                    :disabled="!studentsToRemind"
                    @click="submitUpdatePendingStudent"
                >
                    Resend {{ studentsToRemind.length }} Invite(s)
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>


.ri {
    &__top {
        position: relative;
        background: $barely-background;
        padding: 36px 32px 22px;
    }

    &__bottom {
        padding: 45px 32px 36px;

        .ri__personalize {
            p,
            label {
                margin-left: 12px !important;
            }

            p {
                margin-bottom: 16px;
            }
        }
    }

    &__title {
        font-size: 14px;
        font-weight: 600;
        line-height: 19px;
        margin-bottom: 23px;
        color: $slate-03;
        margin-left: 12px;
        position: relative;
        z-index: 1;
    }

    &__halftone {
        position: absolute;
        right: 30px;
        top: 0;
    }

    &__display-name {
        margin-bottom: 16px;

        .ri__display-name-toggle {
            display: flex;
            align-items: center;
            margin-bottom: 24px;

            span {
                display: inline-block;
                font-size: 14px;
                margin-left: 8px;
                font-weight: 600;
                color: $slate-03;
            }
        }

        .ri__display-name-fields {
            display: flex;
            justify-content: space-between;
            margin-bottom: 16px;

            .input {
                flex: 1;

                &:first-child {
                    margin-right: 6px;
                }

                &:last-child {
                    margin-left: 6px;
                }
            }
        }
    }

    &__name {
        display: flex;
        justify-content: space-between;
        margin-bottom: 16px;

        .input {
            flex: 1;

            &:first-child {
                margin-right: 6px;
            }

            &:last-child {
                margin-left: 6px;
            }
        }
    }

    &__email {
        margin-bottom: 45px;
    }

    &__label {
        font-weight: 600;
    }

    &__note {
        font-size: 14px;
        margin-top: 7px;
    }

    &__whats-next {
        position: relative;
        margin-top: 40px;
        padding: 14px 12px 12px 15px;
        background-color: rgba($sky-blue, 0.3);
        border-radius: 3px;
    }

    &__whats-next-title {
        font-weight: 600;
        font-size: 15px;
        margin-bottom: 9px;
        line-height: 20px;
    }

    &__whats-next-text {
        font-size: 14px;
        line-height: 19.12px;
    }
}
</style>
